<!-- MarketingSectionsCountHorizontal.vue -->
<script setup>
const pricePer = ref(250);

const count = ref(0);

function chooseExact(v) {
  count.value = v;
}

const price = computed(() => pricePer.value * Number(count.value));
defineExpose({
  count,
  price,
});
</script>

<template>
  <section>
    <article class="grid gap-8 md:grid-cols-12">
      <!-- LEFT (3/4): hierarchia + grafika -->
      <div class="md:col-span-8">
        <header class="mb-3">
          <h2 class="text-2xl font-semibold tracking-tight text-slate-900">
            Przyciąganie uwagi Twoich klientów to podstawa skutecznej strony
          </h2>
          <p class="mt-2 text-slate-600">
            Sekcje marketingowe to elementy strony, które tłumaczą klientowi, dlaczego
            warto Ci zaufać, co zyska i co ma zrobić dalej. Dzięki nim więcej osób klika,
            pyta i kupuje. To nie ładne słowa — to sprawdzone treści, które naprawdę
            działają.
          </p>
        </header>
        <div class="space-y-4 mt-12">
          <p class="mb-3 text-lg text-slate-600 font-bold">Wybierz liczbę sekcji</p>

          <div class="grid gap-3 grid-cols-1 md:grid-cols-2">
            <!-- MINI = 1 -->
            <button
              type="button"
              @click="chooseExact(1)"
              :aria-pressed="Number(count) === 1"
              class="group relative flex w-full items-center gap-4 rounded-2xl cursor-pointer border border-slate-200 bg-white p-4 shadow-sm transition hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
              :class="Number(count) === 1 ? 'ring-2 ring-slate-900 bg-slate-50' : ''"
            >
              <!-- icon tile -->
              <span
                class="grid w-12 aspect-square place-items-center rounded-lg bg-gradient-to-br from-slate-900 to-slate-700 text-white shadow"
              >
                <!-- leaf icon -->
                <svg
                  viewBox="0 0 24 24"
                  class="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M3 21s6-1 10-5 5-10 5-10-6 1-10 5-5 10-5 10Z" />
                  <path d="M9 15c-1.5-4 1-7 8-9" />
                </svg>
              </span>

              <div class="min-w-0 flex-1">
                <div class="flex items-baseline justify-between gap-3">
                  <span class="truncate text-base font-semibold text-slate-900"
                    >Mini</span
                  >
                  <span
                    class="shrink-0 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-700"
                    >1</span
                  >
                </div>
              </div>
            </button>

            <!-- STANDARD = 5 -->
            <button
              type="button"
              @click="chooseExact(5)"
              :aria-pressed="Number(count) === 5"
              class="group relative flex w-full items-center gap-4 rounded-2xl cursor-pointer border border-slate-200 bg-white p-4 shadow-sm transition hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
              :class="Number(count) === 5 ? 'ring-2 ring-slate-900 bg-slate-50' : ''"
            >
              <span
                class="grid w-12 aspect-square place-items-center rounded-lg bg-gradient-to-br from-slate-900 to-slate-700 text-white shadow"
              >
                <!-- target icon -->
                <svg
                  viewBox="0 0 24 24"
                  class="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="8" />
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2M12 20v2M2 12h2M20 12h2" />
                </svg>
              </span>

              <div class="min-w-0 flex-1">
                <div class="flex items-baseline justify-between gap-3">
                  <span class="truncate text-base font-semibold text-slate-900"
                    >Standard</span
                  >
                  <span
                    class="shrink-0 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-700"
                    >5</span
                  >
                </div>
              </div>
            </button>

            <!-- PRO = 20 -->
            <button
              type="button"
              @click="chooseExact(15)"
              :aria-pressed="Number(count) === 15"
              class="group relative flex w-full items-center gap-4 rounded-2xl cursor-pointer border border-slate-200 bg-white p-4 shadow-sm transition hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
              :class="Number(count) === 15 ? 'ring-2 ring-slate-900 bg-slate-50' : ''"
            >
              <span
                class="grid w-12 aspect-square place-items-center rounded-lg bg-gradient-to-br from-slate-900 to-slate-700 text-white shadow"
              >
                <!-- rocket icon -->
                <svg
                  viewBox="0 0 24 24"
                  class="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M5 19c1.5-4 5-7 9-9l2-6 4 4-6 2c-2 4-5 7-9 9Z" />
                  <path d="M4 20c1-1 2-1 3 0s1 2 0 3-2 1-3 0-1-2 0-3Z" />
                </svg>
              </span>

              <div class="min-w-0 flex-1">
                <div class="flex items-baseline justify-between gap-3">
                  <span class="truncate text-base font-semibold text-slate-900">Pro</span>
                  <span
                    class="shrink-0 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-700"
                    >15</span
                  >
                </div>
              </div>
            </button>

            <div class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p class="mb-3 text-sm text-slate-600 text-center font-bold">
                Liczba sekcji
              </p>

              <!-- Ring -->

              <!-- Stepper -->
              <div class="mt-4 flex items-center justify-center gap-2">
                <button
                  type="button"
                  class="grid size-9 place-items-center rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 cursor-pointer"
                  @click="chooseExact(Math.max(0, Number(count) - 1))"
                  aria-label="Mniej"
                >
                  −
                </button>

                <div class="mx-auto grid place-items-center">
                  <div
                    class="relative grid size-20 place-items-center rounded-full transition"
                    :style="{
                      background:
                        'conic-gradient(#0f172a ' +
                        ((Number(count) * 36) % 360) +
                        'deg, #e2e8f0 0deg)',
                    }"
                  >
                    <div class="absolute inset-2 rounded-full bg-white"></div>
                    <div class="relative z-10 text-3xl font-bold text-slate-900">
                      {{ count }}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  class="grid size-9 place-items-center rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 cursor-pointer"
                  @click="chooseExact(Number(count) + 1)"
                  aria-label="Więcej"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          <div class="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p class="text-sm text-slate-600">Cena</p>
            <p class="mt-1 text-lg font-semibold text-slate-900">
              {{ pricePer }} zł
              <span class="text-sm font-normal text-slate-600">/ sekcję</span>
            </p>
          </div>
        </div>
      </div>

      <!-- RIGHT (1/4): wybór + cena -->
      <aside class="md:col-span-4">
        <!-- Tekst właściwy (pogrubienia wewnątrz akapitów) -->
        <div class="space-y-5 text-[17px] leading-relaxed text-slate-700">
          <h4 class="text-lg font-semibold">
            Dzięki sekcjom marketingowym możesz uzyskać
          </h4>

          <!-- Karty efektów (hierarchia, bez ikon/emoji) -->
          <div class="mt-4 grid gap-3 sm:grid-cols-2">
            <div
              class="p-6 border border-slate-300 shadow rounded-2xl flex flex-col gap-2"
            >
              <div
                class="flex flex-row text-3xl font-extrabold justify-center items-center"
              >
                <CountTo :to="2" />x
              </div>
              <p class="text-sm text-center font-bold">Więcej klientów</p>
            </div>

            <div
              class="p-6 border border-slate-300 shadow rounded-2xl flex flex-col gap-2"
            >
              <div
                class="flex flex-row text-3xl font-extrabold justify-center items-center"
              >
                <CountTo :to="3" />x
              </div>
              <p class="text-sm text-center font-bold">Więcej kliknięć</p>
            </div>

            <div
              class="p-6 border border-slate-300 shadow rounded-2xl flex flex-col gap-2"
            >
              <div
                class="flex flex-row text-3xl font-extrabold justify-center items-center"
              >
                <CountTo :to="68" />%
              </div>
              <p class="text-sm text-center font-bold">Wzrostu zaufania</p>
            </div>

            <div
              class="p-6 border border-slate-300 shadow rounded-2xl flex flex-col gap-2"
            >
              <div
                class="flex flex-row text-3xl font-extrabold justify-center items-center"
              >
                <CountTo :to="128" />%
              </div>
              <p class="text-sm text-center font-bold">Więcej zamówień</p>
            </div>
          </div>
        </div>

        <!-- Hero z subtelną grafiką -->
        <div
          class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 p-6 text-white shadow mt-8"
        >
          <!-- delikatny pattern -->
          <svg
            class="pointer-events-none absolute -right-10 -top-10 h-40 w-40 opacity-20"
            viewBox="0 0 200 200"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stop-color="white" />
                <stop offset="100%" stop-color="#bcd" />
              </linearGradient>
            </defs>
            <circle cx="60" cy="60" r="60" fill="url(#g)" />
            <circle cx="150" cy="120" r="40" fill="url(#g)" />
          </svg>

          <h2 class="text-2xl font-semibold tracking-tight">
            Nie wystarczy mieć stronę. Trzeba umieć przyciągać uwagę.
          </h2>
          <p class="mt-2 max-w-3xl text-[15px] leading-relaxed text-white/85">
            Dzięki analizie Twojej firmy i Twoich klientów możemy dodać do Twojej strony
            <strong> specjalne sekcje, </strong>
            które pomagają przekonać odwiedzającego, że warto się z Tobą skontaktować albo
            złożyć zamówienie.
          </p>
        </div>
      </aside>
    </article>
  </section>
</template>
